import { zodResolver } from "@hookform/resolvers/zod";
import { createFeedbackBodyZod } from "@nlc/shared-types";
import { Paperclip, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import type { CreateFeedbackFormData } from "@/features/request/feedback-tool/feedbackTool/hooks/useCreateFeedback.tsx";

import { Button } from "@/components/button";
import { toast } from "@/components/toast/toast-queue.ts";
import { FileUploaded } from "@/features/request/feedback-tool/feedbackTool/components/FileUploaded.tsx";
import { Loading } from "@/features/request/feedback-tool/feedbackTool/components/Loading.tsx";
import { TextareaEditor } from "@/features/request/feedback-tool/feedbackTool/components/TextareaComment.tsx";
import { useCreateFeedback } from "@/features/request/feedback-tool/feedbackTool/hooks/useCreateFeedback.tsx";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { useLoadingMutation } from "@/features/request/feedback-tool/feedbackTool/hooks/useLoadingMutation.tsx";
import { useUploadAssets } from "@/features/request/feedback-tool/feedbackTool/hooks/useUploadAssets.tsx";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import { useUser } from "@/hooks/useUser.tsx";

export function Form() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { data } = useUser();
  const [createError, setCreateError] = useState("");
  const [renderAssets, setRenderAssets] = useState<
    Array<{ attachmentId: number; name: string }>
  >([]);
  const { data: request } = useGetRequest();
  const requestId = request?.id;

  const {
    formFeedBack,
    img,
    setFormFeedBack: setFormFeedback,
  } = useFeedbackTool();

  const formMethods = useForm<CreateFeedbackFormData>({
    defaultValues: {
      assets: [],
      attachments: [],
      comment: "",
      coordinates: "",
      parentId: img.id,
      userId: data?.user.id,
    },
    resolver: zodResolver(createFeedbackBodyZod),
  });

  const assets = formMethods.watch("assets") || [];

  const { handleFileChange, handleFileDelete } = useUploadAssets(
    "assets",
    assets,
    () => {},
    formMethods,
  );

  const isUploading = assets.some(
    (item) =>
      (item as { status: "error" | "success" | "uploading" }).status ===
      "uploading",
  );

  const isError = assets.some(
    (item) =>
      (item as { status: "error" | "success" | "uploading" }).status ===
      "error",
  );

  const { mutate } = useCreateFeedback(img.id, Number(requestId));
  const isLoading = useLoadingMutation("CREATE", img.id);

  useEffect(() => {
    if (formFeedBack) {
      formMethods.setValue(
        "coordinates",
        `${formFeedBack?.y}%,${formFeedBack?.x}%,${formFeedBack.timestamp}`,
      );
    }
  }, [formFeedBack]);

  useEffect(() => {
    if (assets.length > 0) {
      const removeErrors = assets.filter(
        (item) =>
          (item as { status: "error" | "success" | "uploading" }).status !==
          "error",
      );

      const includeAttachments = removeErrors.map((att) => ({
        attachmentId: att.attachmentId ?? 0,
        name: (att as { file: File }).file.name,
      }));

      setRenderAssets(includeAttachments);
      formMethods.setValue("attachments", includeAttachments);

      setFormFeedback({
        attachments: includeAttachments,
        description: formFeedBack?.description,
        format: "AUDIO",
        id: 0,
        imgId: img.id,
        storyId: 0,
        timestamp: formFeedBack?.timestamp,
        type: "ADD",
        user: {
          id: 0,
          name: "",
        },
        x: 0,
        y: 0,
      });
    }
  }, [assets]);

  useEffect(() => {
    if (isError) {
      toast.add(
        {
          description: "Unsupported file extension.",
          title: "Unsupported Media Type",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    }
  }, [isError]);

  useEffect(() => resetForm(), [img.id]);

  const resetForm = () => {
    formMethods.reset({
      assets: [],
      attachments: [],
      comment: "",
      coordinates: "",
      parentId: img.id,
      userId: data?.user.id ?? 0,
    });
  };

  if (formFeedBack?.format !== "AUDIO" || formFeedBack?.type !== "ADD")
    return null;

  return (
    <div
      className="bg-bg-primary w-full max-w-80 p-4 rounded-md shadow-lg mt-3"
      data-name="box-audio"
    >
      <div className="flex items-start">
        <Controller
          control={formMethods.control}
          name="comment"
          render={({ field: { name, onChange, value } }) => (
            <TextareaEditor name={name} onChange={onChange} value={value} />
          )}
        />
        <Button
          aria-label="Scope help"
          className="h-min cursor-default"
          onPress={() => setFormFeedback(null)}
          variant="unstyled"
        >
          <X className="text-slate-400 mb-3 cursor-pointer" size={24} />
        </Button>
      </div>
      <span className="block text-sm font-normal text-text-error-primary mt-sm">
        {createError}
      </span>
      <div>
        {renderAssets.length > 0
          ? renderAssets.map((asset) => (
              <FileUploaded
                key={asset.attachmentId}
                name={asset.name}
                onDelete={() => handleFileDelete(asset?.attachmentId ?? 0)}
              />
            ))
          : null}
      </div>
      <div className="flex mt-4">
        <Button
          className="bg-base"
          onPress={() => {
            inputRef.current?.click();
          }}
          variant="secondary"
        >
          {isUploading ? (
            <Loading />
          ) : (
            <>
              <input
                className="hidden"
                multiple
                onChange={handleFileChange}
                ref={inputRef}
                type="file"
              />
              <Paperclip className="text-slate-400 cursor-pointer" size={24} />
            </>
          )}
        </Button>
        <Button
          className="w-full ml-2"
          isDisabled={!!isLoading}
          onPress={() => {
            mutate(formMethods.getValues(), {
              onError: (err: unknown) => {
                const comment =
                  "body/comment must NOT have fewer than 1 characters";
                const message = err instanceof Error ? err.message : "";

                if (message === comment) {
                  setCreateError("Message is required");

                  return toast.add(
                    {
                      description: "Message is required",
                      title: "Feedback not updated",
                      type: "warning",
                    },
                    {
                      timeout: 5000,
                    },
                  );
                }
                toast.add(
                  {
                    description: message,
                    title: "Feedback not updated",
                    type: "error",
                  },
                  {
                    timeout: 5000,
                  },
                );
              },
              onSuccess: () => {
                resetForm();
                setRenderAssets([]);
                setFormFeedback(null);
                setCreateError("");
              },
            });
          }}
          type="button"
        >
          {isLoading ? <Loading /> : "Add Feedback"}
        </Button>
      </div>
    </div>
  );
}
